import { Navigate, useNavigate } from "react-router-dom";
import { Button, CircularProgress } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import OnboardingWizard from "views/public/referralOnboarding/OnboardingWizard";
import { ADMIN_HOME, PARTNER_HOME, currentUserIsStaffInPartnerPortal } from "utils/referralRouting";
import { signOut } from "utils/signOut";
import { fetchPartnerEnrolment } from "../../api/endpoints/enrolment";
import { partnerQueryKeys } from "../../api/queryKeys";
import usePartnerQuery from "../../hooks/usePartnerQuery";
import staffOnboardingAdapter from "./staffOnboardingAdapter";

const Shell = ({ children }) => (
  <div className="min-h-screen bg-[#eef3f1] flex items-center justify-center px-4 py-10">
    <div className="w-full max-w-xl rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
      {children}
    </div>
  </div>
);

/**
 * Where someone already signed in finishes their partner profile: a colleague who starts referring,
 * or a referral partner whose referrals were migrated before the portal existed. A new outside
 * partner is onboarded through an invitation instead. Both go through the same wizard here, without
 * the password step, and whatever their account already holds is filled in and locked. An active
 * colleague whose profile still lacks details comes back here to complete it. Anyone the backend
 * will not let through is told why, with a way out.
 */
const StartPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const staff = currentUserIsStaffInPartnerPortal();

  const { data, error, isLoading } = usePartnerQuery(
    partnerQueryKeys.enrolment(),
    fetchPartnerEnrolment,
    { retry: false, refetchOnMount: true }
  );

  if (isLoading) {
    return (
      <Shell>
        <div className="flex justify-center py-6">
          <CircularProgress />
        </div>
      </Shell>
    );
  }

  if (error?.response?.status === 401) return <Navigate to="/login" replace />;
  if (data?.enrolled && data?.detailsComplete) return <Navigate to={PARTNER_HOME} replace />;

  const leave = () => (staff ? navigate(ADMIN_HOME) : signOut({ queryClient, navigate }));
  const leaveLabel = staff ? "Back to organization dashboard" : "Sign out";

  const canUseWizard = data?.canSelfEnrol || (data?.enrolled && staff);
  if (error || !canUseWizard) {
    const message = error
      ? error?.response?.data?.message || "The referral portal is not available right now."
      : data?.blockedReason;
    return (
      <Shell>
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-teal-700 m-0">
          Referral portal
        </p>
        <h1 className="text-2xl font-bold text-slate-900 m-0 mt-1">
          {staff ? "Manage your own referrals" : "Your partner account"}
        </h1>
        <p className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-4 py-3 mt-5 mb-0">
          {message || "The referral portal is not available for this account."}
        </p>
        <Button size="small" onClick={leave} sx={{ mt: 1.5 }}>
          {leaveLabel}
        </Button>
      </Shell>
    );
  }

  return (
    <OnboardingWizard
      adapter={staffOnboardingAdapter}
      copy={{
        headline: () => (data?.enrolled ? "Complete your partner profile" : "Start referring"),
        intro:
          (data?.status === "PRE_ENROLMENT"
            ? "Your earlier referrals are already linked to you — finish these details to see them in the portal. "
            : "") +
          "Details already on your account are filled in and locked. Add the rest so the office can verify you and pay your referrals.",
        failureTitle: "Referral portal unavailable",
        submitLabel: data?.enrolled ? "Save my details" : "Start referring",
      }}
      onDone={() => {
        queryClient.invalidateQueries(partnerQueryKeys.all);
        toast.success(
          data?.enrolled ? "Your partner details are complete" : "You can now manage your referrals"
        );
        navigate(PARTNER_HOME, { replace: true });
      }}
      asideFooter={
        <button
          type="button"
          onClick={leave}
          className="mt-2 bg-transparent border-0 p-0 text-xs font-semibold text-sky-600 lg:text-sky-200 underline cursor-pointer"
        >
          {leaveLabel}
        </button>
      }
    />
  );
};

export default StartPage;
